import {
  createBookingAction,
  SandboxBookingInput,
  updateBookingAction,
  redeemBookingAction,
} from './domains/bookings';
import {
  createCommandAction,
  createHostAction,
  moveHostsAction,
  setShellClientAction,
  setServiceModeAction,
  SandboxHostInput,
  updateHostAction,
} from './domains/hosts';
import {
  createPendingPostPaymentAction,
  createPaymentAction,
  ProcessPostPaymentInput,
  processPostPaymentAction,
  refundPaymentAction,
  validatePaymentAction,
} from './domains/payments';
import {
  moveSessionAction,
  startSessionAction,
  StartSessionInput,
  stopSessionAction,
} from './domains/sessions';
import {
  createTaskAction,
  deleteTaskAction,
  SandboxTaskInput,
  setTaskCompletedAction,
  updateTaskAction,
} from './domains/tasks';
import { SandboxDomainError } from './errors';
import { createSeedState } from './seed';
import {
  SandboxBooking,
  SandboxBonusHistoryItem,
  CreatePaymentInput,
  SandboxClient,
  SandboxEvent,
  SandboxHost,
  SandboxPayment,
  SandboxSession,
  SandboxSetting,
  SandboxState,
  SandboxTask,
} from './types';

export { SandboxDomainError } from './errors';

const cloneState = (state: SandboxState): SandboxState =>
  JSON.parse(JSON.stringify(state)) as SandboxState;

export class SandboxStore {
  private state: SandboxState;
  private anchorMs: number;
  private lastRuntimeTimestamp: number;

  constructor(private readonly now: () => number = Date.now) {
    this.anchorMs = this.readClock();
    this.lastRuntimeTimestamp = this.anchorMs - 1;
    this.state = createSeedState(this.anchorMs);
  }

  getState(): SandboxState {
    return this.state;
  }

  reset(): void {
    this.anchorMs = this.readClock();
    this.lastRuntimeTimestamp = this.anchorMs - 1;
    this.state = createSeedState(this.anchorMs);
  }

  getAnchorMs(): number {
    return this.anchorMs;
  }

  getCurrentTimestamp(): string {
    return new Date(
      Math.max(this.readClock(), this.lastRuntimeTimestamp)
    ).toISOString();
  }

  getWorker(): SandboxState['worker'] {
    return this.state.worker;
  }

  setSetting(alias: string, value: string | null): SandboxSetting {
    return this.transact((draft) =>
      this.updateSetting(draft, alias, value, this.nextTimestamp(draft))
    );
  }

  setSettings(
    settings: Array<{ alias: string; value: string | null }>
  ): SandboxSetting[] {
    return this.transact((draft) => {
      const timestamp = this.nextTimestamp(draft);
      return settings.map(({ alias, value }) =>
        this.updateSetting(draft, alias, value, timestamp)
      );
    });
  }

  snapshot(): SandboxState {
    return cloneState(this.state);
  }

  getClient(uuid: string): SandboxClient {
    const client = this.state.clients.find((item) => item.uuid === uuid);
    if (!client) throw new SandboxDomainError('Клиент не найден');
    return client;
  }

  setDeposit(uuid: string, value: number): SandboxClient {
    if (!Number.isFinite(value) || value < 0) {
      throw new SandboxDomainError('Баланс должен быть неотрицательным числом');
    }
    const client = this.getClient(uuid);
    client.deposit = value;
    client.updated_at = this.nextTimestamp();
    this.addDepositEvent(client, value, 'DEPOSIT_UPDATED');
    return client;
  }

  setClientBanned(uuid: string, banned: boolean): SandboxClient {
    return this.transact((draft) => {
      const client = draft.clients.find((item) => item.uuid === uuid);
      if (!client) throw new SandboxDomainError('Клиент не найден');
      const timestamp = this.nextTimestamp(draft);
      client.banned_at = banned ? timestamp : null;
      client.updated_at = timestamp;
      this.addAuditEvent(draft, timestamp, {
        type: banned ? 'USER_BANNED' : 'USER_UNBANNED',
        description: banned ? 'Клиент заблокирован' : 'Клиент разблокирован',
        client: this.toEventClient(client),
      });
      return client;
    });
  }

  setBonus(uuid: string, value: number): SandboxClient {
    return this.transact((draft) => {
      if (!Number.isFinite(value) || value < 0) {
        throw new SandboxDomainError(
          'Бонусы должны быть неотрицательным числом'
        );
      }
      const client = draft.clients.find((item) => item.uuid === uuid);
      if (!client) throw new SandboxDomainError('Клиент не найден');
      const oldValue = client.bonus;
      const timestamp = this.nextTimestamp(draft);
      client.bonus = value;
      client.updated_at = timestamp;
      this.addAuditEvent(draft, timestamp, {
        type: 'BONUS_UPDATED',
        description: 'Бонусы клиента изменены',
        client: this.toEventClient(client),
        value1: oldValue,
        value2: value,
      });
      return client;
    });
  }

  setUserDiscount(uuid: string, value: number): SandboxClient {
    return this.transact((draft) => {
      if (!Number.isFinite(value) || value < 0 || value > 100) {
        throw new SandboxDomainError('Скидка должна быть от 0 до 100%');
      }
      const client = draft.clients.find((item) => item.uuid === uuid);
      if (!client) throw new SandboxDomainError('Клиент не найден');
      const oldValue =
        client.discounts.find((discount) => discount.type === 'PERSONAL')
          ?.value ?? client.user_discount;
      const timestamp = this.nextTimestamp(draft);
      const personalDiscount = client.discounts.find(
        (discount) => discount.type === 'PERSONAL'
      );
      if (personalDiscount) personalDiscount.value = value;
      else client.discounts.push({ type: 'PERSONAL', value });
      client.user_discount = value;
      client.updated_at = timestamp;
      this.addAuditEvent(draft, timestamp, {
        type: 'USER_DISCOUNT_UPDATED',
        description: 'Персональная скидка клиента изменена',
        client: this.toEventClient(client),
        value1: oldValue,
        value2: value,
      });
      return client;
    });
  }

  getBonusHistory(uuid: string): SandboxBonusHistoryItem[] {
    this.getClient(uuid);
    return this.state.events
      .filter(
        (event) => event.type === 'BONUS_UPDATED' && event.client?.uuid === uuid
      )
      .map((event) => {
        const oldValue = Number(event.value1 ?? 0);
        const newValue = Number(event.value2 ?? oldValue);
        const delta = newValue - oldValue;
        return {
          type: 'MANAGER',
          worker_first_name: event.worker.first_name,
          worker_last_name: event.worker.last_name,
          created_at: event.timestamp,
          add: delta > 0,
          new_value: newValue,
          old_value: oldValue,
          delta,
        };
      });
  }

  createPayment(input: CreatePaymentInput): SandboxPayment {
    return this.transact((draft) =>
      createPaymentAction(draft, input, this.nextTimestamp(draft), draft.worker)
    );
  }

  validatePayment(input: CreatePaymentInput): boolean {
    validatePaymentAction(this.state, input);
    return true;
  }

  refundPayment(id: number): SandboxPayment {
    return this.transact((draft) =>
      refundPaymentAction(draft, id, this.nextTimestamp(draft), draft.worker)
    );
  }

  createBooking(input: SandboxBookingInput): SandboxBooking {
    return this.transact((draft) => {
      const booking = createBookingAction(draft, input);
      this.addAuditEvent(draft, this.nextTimestamp(draft), {
        type: 'BOOKING_CREATED',
        description: `Создана бронь №${booking.id}`,
        client: this.eventClientByUuid(draft, booking.client?.uuid),
        booking: this.bookingReference(booking),
        hosts: this.hostReferences(draft, booking.hostIds),
      });
      return booking;
    });
  }

  updateBooking(id: number, input: SandboxBookingInput): SandboxBooking {
    return this.transact((draft) => {
      const booking = updateBookingAction(draft, id, input);
      const canceled = booking.status === 'CANCELED';
      this.addAuditEvent(draft, this.nextTimestamp(draft), {
        type: canceled ? 'BOOKING_CANCELED' : 'BOOKING_UPDATED',
        description: canceled
          ? `Отменена бронь №${booking.id}`
          : `Обновлена бронь №${booking.id}`,
        client: this.eventClientByUuid(draft, booking.client?.uuid),
        booking: this.bookingReference(booking),
        hosts: this.hostReferences(draft, booking.hostIds),
      });
      return booking;
    });
  }

  redeemBooking(id: number): SandboxBooking {
    return this.transact((draft) => {
      const booking = redeemBookingAction(draft, id);
      this.addAuditEvent(draft, this.nextTimestamp(draft), {
        type: 'BOOKING_CONFIRMED',
        description: `Погашена бронь №${booking.id}`,
        client: this.eventClientByUuid(draft, booking.client?.uuid),
        booking: this.bookingReference(booking),
        hosts: this.hostReferences(draft, booking.hostIds),
      });
      return booking;
    });
  }

  setServiceMode(id: number, flag: boolean): SandboxHost {
    return this.transact((draft) => {
      const host = setServiceModeAction(draft, id, flag);
      const hosts = this.hostReferences(draft, [host.id]);
      const commandTitle = flag ? 'SERVICE_MODE_ON' : 'SERVICE_MODE_OFF';
      this.addAuditEvent(draft, this.nextTimestamp(draft), {
        type: 'SEND_HOST_COMMAND',
        description: flag
          ? 'Режим обслуживания включен'
          : 'Режим обслуживания выключен',
        client: null,
        host: { title: hosts[0].title },
        hosts,
        host_command: { title: commandTitle },
      });
      return host;
    });
  }

  moveHosts(input: Parameters<typeof moveHostsAction>[1]): SandboxHost[] {
    return this.transact((draft) => moveHostsAction(draft, input));
  }

  createHost(input: SandboxHostInput): SandboxHost {
    return this.transact((draft) => createHostAction(draft, input));
  }

  updateHost(id: number, input: SandboxHostInput): SandboxHost {
    return this.transact((draft) => updateHostAction(draft, id, input));
  }

  createTask(input: SandboxTaskInput): SandboxTask {
    return this.transact((draft) =>
      createTaskAction(draft, input, this.nextTimestamp(draft))
    );
  }

  updateTask(id: number, input: SandboxTaskInput): SandboxTask {
    return this.transact((draft) =>
      updateTaskAction(draft, id, input, this.nextTimestamp(draft))
    );
  }

  deleteTask(id: number): SandboxTask {
    return this.transact((draft) => deleteTaskAction(draft, id));
  }

  setTaskCompleted(id: number, value: boolean): SandboxTask {
    return this.transact((draft) =>
      setTaskCompletedAction(draft, id, value, this.nextTimestamp(draft))
    );
  }

  setShellClient(hostId: number, clientUuid: string | null): SandboxHost {
    return this.transact((draft) =>
      setShellClientAction(draft, hostId, clientUuid)
    );
  }

  createCommand(
    input: Parameters<typeof createCommandAction>[1]
  ): ReturnType<typeof createCommandAction> {
    return this.transact((draft) => {
      const command = createCommandAction(draft, input);
      const hostIds = input.host_ids ?? (input.host_id ? [input.host_id] : []);
      const hosts = this.hostReferences(draft, hostIds);
      this.addAuditEvent(draft, this.nextTimestamp(draft), {
        type: 'SEND_HOST_COMMAND',
        description: `Команда ${command.action} отправлена локально`,
        client: null,
        host: hosts[0] ? { title: hosts[0].title } : undefined,
        hosts,
        host_command: { title: command.action },
      });
      return command;
    });
  }

  startSession(input: StartSessionInput): SandboxSession {
    return this.transact((draft) => {
      const timestamp = this.nextTimestamp(draft);
      const session = startSessionAction(draft, input, timestamp);
      this.addSessionAuditEvent(
        draft,
        session,
        timestamp,
        'CLIENT_SESSION_STARTED',
        'Сеанс запущен'
      );
      return session;
    });
  }

  moveSession(id: number, hostId: number): SandboxSession {
    return this.transact((draft) => {
      const previousHostId = draft.sessions.find(
        (session) => session.id === id && session.status === 'ACTIVE'
      )?.host_id;
      const session = moveSessionAction(draft, id, hostId);
      this.addSessionAuditEvent(
        draft,
        session,
        this.nextTimestamp(draft),
        'CLIENT_SESSION_HOST_CHANGED',
        'Сеанс перенесен',
        previousHostId
      );
      return session;
    });
  }

  stopSession(id: number): SandboxSession {
    return this.transact((draft) => {
      const timestamp = this.nextTimestamp(draft);
      const session = stopSessionAction(draft, id, timestamp);
      this.addSessionAuditEvent(
        draft,
        session,
        timestamp,
        'CLIENT_SESSION_FINISHED',
        'Сеанс завершен'
      );
      return session;
    });
  }

  stopSessionWithPayment(id: number): {
    session: SandboxSession;
    payment: SandboxPayment | null;
  } {
    return this.transact((draft) => {
      const timestamp = this.nextTimestamp(draft);
      const session = stopSessionAction(draft, id, timestamp);
      const payment = createPendingPostPaymentAction(draft, session, timestamp);
      this.addSessionAuditEvent(
        draft,
        session,
        timestamp,
        'CLIENT_SESSION_FINISHED',
        'Сеанс завершен'
      );
      return { session, payment };
    });
  }

  processPostPayment(
    id: number,
    input: ProcessPostPaymentInput
  ): SandboxPayment {
    return this.transact((draft) =>
      processPostPaymentAction(
        draft,
        id,
        input,
        this.nextTimestamp(draft),
        draft.worker
      )
    );
  }

  deleteClient(uuid: string): void {
    const client = this.getClient(uuid);
    if (client.origin === 'preset') {
      throw new SandboxDomainError(
        'Предустановленные сущности нельзя удалить в локальной песочнице'
      );
    }
    this.state.clients = this.state.clients.filter(
      (item) => item.uuid !== uuid
    );
  }

  private addDepositEvent(
    client: SandboxClient,
    value: number,
    type: string
  ): void {
    this.state.events.unshift({
      timestamp: this.nextTimestamp(),
      type,
      description: 'Баланс клиента изменен локально',
      additional_data: null,
      additional_seconds: null,
      client: this.toEventClient(client),
      worker: this.state.worker,
      deposit: value,
    });
  }

  private updateSetting(
    state: SandboxState,
    alias: string,
    value: string | null,
    updatedAt: string
  ): SandboxSetting {
    const setting = state.settings.find((item) => item.alias === alias);
    if (setting) {
      setting.value = value ?? '';
      setting.updated_at = updatedAt;
      return setting;
    }

    const created: SandboxSetting = {
      id: state.settings.reduce((max, item) => Math.max(max, item.id), 0) + 1,
      alias,
      value: value ?? '',
      updated_at: updatedAt,
      disabled: false,
    };
    state.settings.push(created);
    return created;
  }

  private toEventClient(client: SandboxClient): SandboxEvent['client'] {
    return {
      uuid: client.uuid,
      phone: client.phone,
      nickname: client.nickname,
      first_name: client.first_name,
      last_name: client.last_name,
    };
  }

  private eventClientByUuid(
    state: SandboxState,
    uuid?: string
  ): SandboxEvent['client'] {
    const client = uuid
      ? state.clients.find((candidate) => candidate.uuid === uuid)
      : undefined;
    return client ? this.toEventClient(client) : null;
  }

  private hostReferences(
    state: SandboxState,
    hostIds: number[]
  ): NonNullable<SandboxEvent['hosts']> {
    return hostIds.map((id) => {
      const host = state.hosts.find((candidate) => candidate.id === id);
      return { id, value: id, title: host?.alias ?? `Host ${id}` };
    });
  }

  private bookingReference(
    booking: SandboxBooking
  ): NonNullable<SandboxEvent['booking']> {
    return {
      cost: 0,
      from: booking.from,
      id: booking.id,
      to: booking.to,
    };
  }

  private addSessionAuditEvent(
    state: SandboxState,
    session: SandboxSession,
    timestamp: string,
    type: string,
    description: string,
    previousHostId?: number
  ): void {
    const host = this.hostReferences(state, [session.host_id])[0];
    const previousHost = previousHostId
      ? this.hostReferences(state, [previousHostId])[0]
      : undefined;
    const expiresAt = session.duration
      ? new Date(
          Date.parse(session.started_at) + session.duration * 1000
        ).toISOString()
      : null;
    this.addAuditEvent(state, timestamp, {
      type,
      description,
      client: this.eventClientByUuid(state, session.client_uuid ?? undefined),
      host: { title: previousHost?.title ?? host.title },
      host2: previousHost ? { title: host.title } : undefined,
      client_session: {
        elapsed: session.elapsed,
        expires_at: expiresAt,
        finished_at: session.finished_at,
        id: session.id,
        time_left: session.time_left,
        is_per_minute:
          session.postpaid || session.seances[0]?.tariff.kind === 'PER_MINUTE',
        total_cost: session.total_cost,
      },
    });
  }

  private addAuditEvent(
    state: SandboxState,
    timestamp: string,
    event: Omit<
      SandboxEvent,
      'timestamp' | 'worker' | 'additional_data' | 'additional_seconds'
    >
  ): void {
    state.events.unshift({
      timestamp,
      worker: state.worker,
      additional_data: null,
      additional_seconds: null,
      ...event,
    });
  }

  private transact<T>(action: (draft: SandboxState) => T): T {
    const draft = cloneState(this.state);
    const result = action(draft);
    this.state = draft;
    return result;
  }

  private nextTimestamp(state: SandboxState = this.state): string {
    state.nextEventOffset += 1;
    const currentTimestamp = this.readClock();
    const nextTimestamp = Math.max(
      currentTimestamp,
      this.lastRuntimeTimestamp + 1
    );
    this.lastRuntimeTimestamp = nextTimestamp;
    return new Date(nextTimestamp).toISOString();
  }

  private readClock(): number {
    const timestamp = this.now();
    return Number.isFinite(timestamp) ? Math.trunc(timestamp) : Date.now();
  }
}

export const sandboxStore = new SandboxStore();
